import React, { ChangeEvent, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Redirect, Link, useLocation } from 'react-router-dom';

// Material-UI components
import {
    Box,
    Button,
    Checkbox,
    FormControlLabel,
    Grid,
    Typography,
    TextField,
    IconButton,
    InputAdornment,
} from '@mui/material';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { BrandedCardContainer } from '../../components/BrandedCardContainer/BrandedCardContainer';

// Constants
import { EMAIL_REGEX, CONTACT_EMAIL, CONTACT_PHONENO } from '../../constants';
import * as PXBColors from '@brightlayer-ui/colors';
import BrandLogo from '../../assets/images/Logo_White_Screen.svg';
import cyberCertifyLogo from '../../assets/images/cybersecurity_certified.svg';
import {
    useAuthState,
    isAuthenticated,
    useAuthHelper,
    getRememberMe,
    getUserEmail,
} from '../../components/AuthContextProvider';
import { Thunks } from '../../actions';
import { ConfirmationPopUp } from '../../components/ConfirmationPopUp/ConfirmationPopUp';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { Selectors } from '../../selectors';
import { useLanguageLocale } from '../../hooks/language-locale-hooks';

const useStyles = makeStyles((theme: Theme) => ({
    formFields: {
        marginBottom: theme.spacing(5),
    },
    buttonRow: {
        marginBottom: theme.spacing(5),
        flexWrap: 'nowrap',
        [theme.breakpoints.down('md')]: {
            flexWrap: 'wrap',
            flexDirection: 'column-reverse',
            justifyContent: 'center',
        },
    },
    link: {
        fontWeight: 600,
        textTransform: 'none',
    },
    largeIcon: {
        width: 60,
        height: 60,
        color: PXBColors.gray[500],
    },
}));

const Login: React.FC = () => {
    const location: any = useLocation();
    const classes = useStyles();
    const { t } = useLanguageLocale();
    const authState = useAuthState();
    const authHelper = useAuthHelper();
    const dispatch = useDispatch();
    const inActiveLogoutMessage = useSelector(Selectors.Dashboard.inActiveLogOut);
    const errorMessage = useSelector(Selectors.Authentication.errorMessage);
    const rememberMeChecked = getRememberMe(authState);
    const rememberMeEmail = getUserEmail(authState) || '';
    const [inActiveLogout, setInactiveLogOut] = useState(inActiveLogoutMessage);
    const [showPassword, setShowPassword] = useState(false);
    const [showDialog, setShowDialog] = useState(false);
    const [email, setEmail] = useState<string>(rememberMeEmail);
    const [password, setPassword] = useState('');
    const [remember, setRemember] = useState(rememberMeChecked);

    if (isAuthenticated(authState)) {
        const { from } =
            location && location.state && 'from' in location.state ? location.state : { from: { pathname: '/' } };
        return <Redirect to={from} />;
    }
    const validEmail = (emailId: string): boolean =>
        Boolean(
            emailId &&
                typeof emailId === 'string' &&
                emailId.length > 0 &&
                new RegExp(EMAIL_REGEX).test(emailId) &&
                emailId.split('@')[0].length <= 64 &&
                emailId.split('@')[1].length <= 48
        );
    const canLogIn = (): boolean => Boolean(validEmail(email) && password.length > 0);

    const onLogIn = (): void => {
        setInactiveLogOut(false);
        dispatch(Thunks.Authentication.login(email, password, remember, authHelper.onUserAuthenticated));
    };
    const handleClickShowPassword = (): void => {
        setShowPassword(!showPassword);
    };

    const body = (): JSX.Element => (
        <div data-testid="contact-info-dialog">
            <Typography variant="h6" style={{ fontWeight: 'bold' }}>
                General Questions
            </Typography>
            <Typography variant="subtitle1" data-testid="contact-email">
                For feedback,question or support please email us at{' '}
                <a href={`mailto:${CONTACT_EMAIL}`} style={{ textDecoration: 'none' }}>
                    {CONTACT_EMAIL}
                </a>
            </Typography>
            <br></br>
            <Typography variant="h6" style={{ fontWeight: 'bold' }}>
                Emergency Support
            </Typography>
            <Typography variant="subtitle1" data-testid="contact-phone">
                For 24/7 technical support, please call
                <br></br>
                <a href={`tel:${CONTACT_PHONENO}`} style={{ textDecoration: 'none' }}>
                    {CONTACT_PHONENO}
                </a>
            </Typography>
        </div>
    );
    return <>
        <BrandedCardContainer>
            <>
                <form
                    onSubmit={(evt): void => {
                        evt.preventDefault();
                        onLogIn();
                    }}
                >
                    <div style={{ padding: '15px' }} data-testid="login-form">
                        <img style={{ maxWidth: 250, margin: 10 }} src={BrandLogo} alt="logo" />
                        {inActiveLogout && (
                            <Typography
                                variant="subtitle1"
                                color="error"
                                style={{ paddingBottom: 10, paddingLeft: 25 }}
                            >
                                {t('INACTIVITY.INACTIVE_LOGOUT')}
                            </Typography>
                        )}
                    </div>
                    <Box style={{ padding: '0px 40px', display: 'flex', flexDirection: 'column' }}  sx={{
                                    '& .MuiTextField-root': { margin: '16px 0px 26px 0px' },
                                }}>
                        <TextField
                            label={t('LABELS.EMAIL')}
                            id="email"
                            name="email"
                            type="email"
                            className={classes.formFields}
                            value={email}
                            onChange={(evt: ChangeEvent<HTMLInputElement>): void => setEmail(evt.target.value)}
                            variant="filled"
                            error={Boolean(errorMessage)}
                            helperText={t(errorMessage)}
                            inputProps={{
                                'data-testid': 'email',
                            }}
                        />
                        <TextField
                            type={showPassword ? 'text' : 'password'}
                            id="password"
                            name="password"
                            label={t('LABELS.PASSWORD')}
                            className={classes.formFields}
                            value={password}
                            onChange={(evt: ChangeEvent<HTMLInputElement>): void => setPassword(evt.target.value)}
                            variant="filled"
                            error={Boolean(errorMessage)}
                            helperText={t(errorMessage)}
                            inputProps={{
                                'data-testid': 'password',
                            }}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            aria-label="Toggle password visibility"
                                            onClick={handleClickShowPassword}
                                            size="large">
                                            {showPassword ? <Visibility /> : <VisibilityOff />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />
                        <Grid
                            container
                            direction="row"
                            alignItems="center"
                            justifyContent="space-between"
                            className={classes.buttonRow}
                        >
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        color="primary"
                                        checked={remember}
                                        data-testid="remember-me"
                                        onChange={(evt): void => setRemember(evt.target.checked)}
                                    />
                                }
                                label={t('ACTIONS.REMEMBER')}
                            />
                            <Button
                                type="submit"
                                variant="contained"
                                disabled={!canLogIn()}
                                color="primary"
                                style={{ width: 150 }}
                                data-testid="login"
                            >
                                {t('ACTIONS.LOG_IN')}
                            </Button>
                        </Grid>

                        <div style={{ textAlign: 'center', paddingBottom: 32 }}>
                            <Typography variant="body2">
                                <Link
                                    className={classes.link}
                                    to="/forgot-password"
                                    data-testid="forgot-password-link"
                                >
                                    {t('LABELS.FORGOT_PASSWORD')}
                                </Link>
                            </Typography>
                            <br />
                            <Typography variant="body2">{t('LABELS.NEED_ACCOUNT')}</Typography>
                            <Typography variant="body2">
                                <Button
                                    color="primary"
                                    data-testid="contact-dialog"
                                    className={classes.link}
                                    onClick={(): void => {
                                        setShowDialog(true);
                                    }}
                                >
                                    {t('MESSAGES.CONTACT')}
                                </Button>
                            </Typography>
                            <br />
                        </div>
                        <div style={{ textAlign: 'center' }} data-testid="cyber-logo">
                            <img src={cyberCertifyLogo} alt="logo" />
                        </div>
                    </Box>
                </form>
            </>
        </BrandedCardContainer>

        {showDialog && (
            <ConfirmationPopUp
                icon={<HelpOutlineIcon className={classes.largeIcon} />}
                body={body()}
                width="xs"
                okButtonText={t('ACTIONS.OKAY')}
                show={showDialog}
                handleOk={(): void => {
                    setShowDialog(false);
                }}
                okButtonShow={true}
            />
        )}
    </>;
};
export { Login };
